import type { AccessPolicy, AccessPolicyUpdate } from "./_shared"

import { api } from "@/lib/api-client"

// MCP Server Management
export interface McpServerStatus {
  label: string
  url: string
  allowed_tools: string[] | null
  require_approval: string
  enabled: boolean
  source: "config" | "override" | "dynamic"
  access_configured?: boolean
  access_overridden?: boolean
  /** Optional HTTP headers merged into the MCP request. */
  headers?: Record<string, string>
  /** Forward-compatible fields the admin may have stored via Raw mode. */
  [key: string]: unknown
}

export interface McpServerConfig {
  label: string
  url: string
  allowed_tools?: string[] | null
  require_approval?: string
  enabled?: boolean
}

export async function getAgentMcps(agentName: string): Promise<McpServerStatus[]> {
  const response = await api.get(`/admin/agents/${agentName}/mcps`)
  return response.data || response
}

export async function addMcpServer(
  agentName: string,
  config: McpServerConfig
): Promise<{
  success: boolean
  agent_name: string
  label: string
  message: string
}> {
  const response = await api.post(`/admin/agents/${agentName}/mcps`, config)
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || "Failed to add MCP server")
  }

  return data
}

export async function updateMcpStatus(
  agentName: string,
  label: string,
  enabled: boolean
): Promise<{
  success: boolean
  agent_name: string
  label: string
  enabled: boolean
  message: string
}> {
  const response = await api.put(
    `/admin/agents/${agentName}/mcps/${encodeURIComponent(label)}/status`,
    { enabled }
  )
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || `Failed to update MCP ${label} status`)
  }

  return data
}

export async function deleteMcpServer(
  agentName: string,
  label: string
): Promise<{
  success: boolean
  agent_name: string
  label: string
  message: string
}> {
  const response = await api.delete(`/admin/agents/${agentName}/mcps/${encodeURIComponent(label)}`)
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || `Failed to delete MCP ${label}`)
  }

  return data
}

export async function getMcpAccessPolicy(agentName: string, label: string): Promise<AccessPolicy> {
  const response = await api.get(
    `/admin/agents/${agentName}/mcps/${encodeURIComponent(label)}/access`
  )
  const data = response.data || response
  return data.access || {}
}

export async function getMcpsBaseAccessPolicy(agentName: string): Promise<AccessPolicy> {
  const response = await api.get(`/admin/agents/${agentName}/mcps/base/access`)
  const data = response.data || response
  return data.access || {}
}

export async function updateMcpAccessPolicy(
  agentName: string,
  label: string,
  policy: AccessPolicyUpdate
): Promise<AccessPolicy> {
  const response = await api.put(
    `/admin/agents/${agentName}/mcps/${encodeURIComponent(label)}/access`,
    policy
  )
  const data = response.data || response
  return data.access || {}
}

export async function updateMcpsBaseAccessPolicy(
  agentName: string,
  policy: AccessPolicyUpdate
): Promise<AccessPolicy> {
  const response = await api.put(`/admin/agents/${agentName}/mcps/base/access`, policy)
  const data = response.data || response
  return data.access || {}
}

export async function clearMcpAccessPolicy(
  agentName: string,
  label: string
): Promise<AccessPolicy> {
  const response = await api.put(
    `/admin/agents/${agentName}/mcps/${encodeURIComponent(label)}/access`,
    {}
  )
  const data = response.data || response
  return data.access || {}
}

// =============================================================================
// MCP server update (backend: routes/admin/agents.py)
// =============================================================================

/**
 * Update an MCP server's configuration in place. The server ignores any
 * ``label`` passed in the body — the URL path is authoritative. Pair with
 * the Form+Raw tabbed edit dialog; Raw tab sets ``source="raw"`` and a
 * ``reason`` so the audit entry is filterable.
 */
export interface UpdateMcpServerArgs {
  agentName: string
  label: string
  /**
   * Partial fields to merge into the MCP entry. The Form tab sends the
   * well-known five (url / allowed_tools / require_approval / headers /
   * enabled). The Raw tab sends the full parsed JSON object, so arbitrary
   * additional keys are allowed — the backend accepts and persists them.
   * ``label`` is ignored in the body (URL path is authoritative).
   */
  updates: {
    url?: string
    allowed_tools?: string[] | null
    require_approval?: string
    headers?: Record<string, string>
    enabled?: boolean
    [key: string]: unknown
  }
  source?: string
  reason?: string
  /**
   * Optimistic-concurrency token from the latest GET .../raw. Required when
   * ``source === "raw"`` (the backend rejects raw saves without it). For
   * Form-tab partial updates, leave undefined.
   */
  expectedHash?: string
}

export async function updateMcpServer(args: UpdateMcpServerArgs): Promise<{
  success: boolean
  agent_name: string
  label: string
  mcp: McpServerConfig & { enabled: boolean }
  before: McpServerConfig & { enabled: boolean }
  hash: string
  message: string
}> {
  const { agentName, label, updates, source, reason, expectedHash } = args
  const body: Record<string, unknown> = { ...updates }
  if (source) body.source = source
  if (reason) body.reason = reason
  if (expectedHash) body.expected_hash = expectedHash

  const response = await api.put(
    `/admin/agents/${encodeURIComponent(agentName)}/mcps/${encodeURIComponent(label)}`,
    body
  )
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || data.error || `Failed to update MCP ${label}`)
  }

  return data
}

export async function getMcpRaw(
  agentName: string,
  label: string
): Promise<{ entity: Record<string, unknown> | null; hash: string }> {
  const response = await api.get(
    `/admin/agents/${encodeURIComponent(agentName)}/mcps/${encodeURIComponent(label)}/raw`
  )
  return response.data || response
}
